const Promise = require('es6-promise');
const queryString = require('query-string');
const Request = require('./request');
const assign = require('./utils/assign');
const randomHash = require('./utils/randomHash');
const Logger = require('./utils/logger');

/**
 *
 * @constructor
 * @param {string} options.client_id
 * @param {string} options.client_secret
 * @param {string} options.authorization_basic
 * @param {string} options.baseURL
 * @param {Object} options.endpoints
 */
function OAuth2Client(options) {
  this.options = assign({}, options);
  this.request = Request.create({
    baseUrl: this.options.baseUrl,
  });
  this.log = new Logger('[spotii-auth.oauth]');
}

/**
 * Build authorization url where user should be redirected in OAuth 2.0 sign in flow.
 *
 * @method buildAuthorizationUrl
 * @param {Object} options
 * @param {String} options.client_id
 * @param {String[]} options.scope
 * @param {String} [options.response_type='code'] Default: `code`
 * @param {String} [options.state] Default: will be generated
 * @param {String} [options.redirect_uri]
 * @param {String} [options.client_secret]
 * @returns {String} URL for authorization services
 */
OAuth2Client.prototype.buildAuthorizationUrl = function(options) {
  const baseUrl = this.options.baseUrl;
  const authorizeUrl = this.options.endpoints.authorize;

  const defaultParams = {
    response_type: 'code',
    state: randomHash(),
    // TODO: what to do with this URL?
    redirect_uri: 'http://127.0.0.1:5556/callback',
  };
  const params = assign(defaultParams, options);
  // Hydra uses RFC1738 for spaces
  const query = queryString.stringify(params).replace(/%20/g, '+');

  return `${baseUrl}${authorizeUrl}?${query}`;
};

/**
 * Build logout url where user should be redirected for logout.
 *
 * @param {String} idHint
 * @param {String} [returnUrl]
 * @returns {String} Redirect URL
 */
OAuth2Client.prototype.buildLogoutUrl = function(idHint, returnUrl) {
  const baseUrl = this.options.baseUrl;
  const logoutUrl = this.options.endpoints.logout;
  const params = {
    id_token_hint: idHint,
    // TODO: what to do with this URL
    post_logout_redirect_uri: returnUrl || 'http://127.0.0.1:5556/logout_callback',
  };

  // Hydra uses RFC1738 for spaces
  const query = queryString.stringify(params).replace(/%20/g, '+');

  return `${baseUrl}${logoutUrl}?${query}`;
};

/**
 * Return promise which will resolve with string of login_challenge.
 *
 * @returns {Promise<String>}
 */
OAuth2Client.prototype.loginChallenge = function() {
  const self = this;
  const authorizationUrl = this.buildAuthorizationUrl({
    client_id: this.options.client_id,
    scope: 'openid offline force_consent',
  });

  this.log('authorizationUrl:', authorizationUrl);

  return (
    this.request('loginChallenge', {
      url: authorizationUrl,
      method: 'GET',
    })
      // Parse `login_challenge` from redirected URL and pass it to the next `.then`.
      .then(function(resp) {
        const url = resp.request.responseURL;
        const urlSearchIndex = url.indexOf('?');
        const urlSearch = urlSearchIndex >= 0 ? url.slice(urlSearchIndex) : '';
        const params = queryString.parse(urlSearch);

        if (!params.login_challenge) {
          const msg = "Authentication service redirect URL doesn't contain `login_challenge`";
          self.log.warn(msg);
          return Promise.reject(new Error(msg));
        }

        return params.login_challenge;
      })
  );
};

/**
 * Authorize user with username and password.
 *
 * @method login
 * @param {String} username
 * @param {String} password
 * @returns {Promise<Token>}
 */
OAuth2Client.prototype.login = function(username, password, kwargs = {}) {
  const self = this;
  return (
    this
      // 1. Get a login challenge first
      .loginChallenge()
      // 2. Then make a call for authentication with login_challenge.
      .then(function(loginChallenge) {
        return self.request('login', {
          url: self.options.endpoints.login,
          method: 'POST',
          data: {
            username: username,
            password: password,
            login_challenge: loginChallenge,
            ...kwargs,
          },
        });
      })
      .then(function(resp) {
        return resp.data;
      })
  );
};

/**
 * Authorize user with token after registration.
 *
 * @method loginWithToken
 * @param {String} token
 * @returns {Promise<Token>}
 */
OAuth2Client.prototype.loginWithToken = function(token, kwargs = {}) {
  const self = this;
  return (
    this
      // 1. Get a login challenge first
      .loginChallenge()
      // 2. Then make a call for authentication with login_challenge.
      .then(function(loginChallenge) {
        return self.request('loginToken', {
          url: self.options.endpoints.loginWithToken,
          method: 'POST',
          data: {
            token: token,
            login_challenge: loginChallenge,
            ...kwargs,
          },
        });
      })
      .then(function(resp) {
        return resp.data;
      })
  );
};
/**
 * Get `access_token` using provided `grant_type`. Supported grant types is `authorization_code`
 * and `refresh_token`. In return will provide promise which resolves into a token data from Hydra.
 *
 * @method token
 * @param {String} grantType One of: 'refresh_token', 'authorization_code'
 * @param {String} refreshToken
 * @returns {Promise<Token>}
 */
OAuth2Client.prototype.token = function(grantType, code) {
  const tokenUrl = this.options.endpoints.token;

  // Check if provided grant_type is valid
  if (!(grantType === 'authorization_code' || grantType === 'refresh_token')) {
    const msg = `Unknown grant type '${grantType}' provided`;
    this.log.warn(msg);
    return Promise.reject(new Error(msg));
  }

  // Build a query string for provided grant_type
  const params = {
    grant_type: grantType,
    [grantType === 'authorization_code' ? 'code' : 'refresh_token']: code,
    scope: 'openid offline',
  };
  // Hydra uses RFC1738 for spaces
  const query = queryString.stringify(params).replace(/%20/g, '+');

  return this.request('token', {
    url: tokenUrl,
    method: 'POST',
    headers: {
      Authorization: `Basic ${this.options.authorizationBasic}`,
    },
    data: query,
  }).then(function(resp) {
    return resp.data;
  });
};

/**
 * Revoking token of provided type. Revoking of `refresh_token` will also revoke `access_token`
 *
 * @param {String} tokenType One of: 'access_token', 'refresh_token'
 * @param {String} token
 * @returns {Promise<Void>}
 */
OAuth2Client.prototype.revokeToken = function(tokenType, token) {
  if (!(tokenType === 'access_token' || tokenType === 'refresh_token')) {
    const msg = `Unknown token type '${tokenType}' provided`;
    this.log.warn(msg);
    return Promise.reject(new Error(msg));
  }

  const revokeUrl = this.options.endpoints.revoke;
  const params = {
    token_type_hint: tokenType,
    token,
  };
  // Hydra uses RFC1738 for spaces
  const query = queryString.stringify(params).replace(/%20/g, '+');

  return this.request('revokeToken', {
    url: revokeUrl,
    method: 'POST',
    headers: {
      Authorization: `Basic ${this.options.authorizationBasic}`,
    },
    data: query,
  });
};

module.exports = OAuth2Client;
